import orderBy from 'lodash/orderBy';
import { createSelector } from 'reselect';
import { activeEncounterMedicationsLoadingSelector } from 'store/medications/selectors';
import { RootState } from 'store/types';
import dayjs from 'dayjs';
import { isBetweenUnbounded } from 'modules/utils/DateUtils';
import * as navSelector from 'store/navigation/selectors';
import { fhirCurrentUserPatientId } from 'store/authentication/selectors';
import { activeVisitVitalsLoadingSelector } from '../vitals/selectors';

const dataSelector = (state: { data: object }) => state.data;

export const visitSummaryListSelector = (state: RootState) => state.visitSummary.visits;

export const visitSummaryListIsSelecting = createSelector(
  visitSummaryListSelector,
  visits => visits.isSelecting
);

export const visitSummaryListSelected = (id: number) =>
  createSelector(visitSummaryListSelector, visits => visits.selected[id]);

export const visitSummaryListAllSelected = createSelector(
  visitSummaryListSelector,
  visits => visits.selected
);

export const visitSummaryListLoadingSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.isFetching
);

export const visitSummaryShortListLoadingSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.isFetchingShortList
);

export const visitSummaryFullListLoadingSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.isFetchingFullList
);

export const visitSummaryShortListErrorSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.errorShortList
);

export const visitSummaryFullListErrorSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.errorFullList
);

export const visitSummaryListTotalCountSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.totalVisits
);

export const visitSummaryShortListRequestStateSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.shortListRequestState
);

export const visitSummaryFullListRequestStateSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.fullListRequestState
);

export const visitSummaryListErrorSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.error
);

export const visitSummaryListDataSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.data
);

export const completedEVisitListSelector = createSelector(visitSummaryListSelector, visits =>
  visits.data.filter(
    item => item.locationObject.locationName === 'CCCL_EVisit' && item.status === 'finished'
  )
);

export const pendingEVisitListSelector = createSelector(visitSummaryListSelector, visits =>
  visits.data.filter(
    item => item.locationObject.locationName === 'CCCL_EVisit' && item.status === 'in-progress'
  )
);

export const visitSummaryFilterSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.filter
);

export const visitSummarySortBySelector = createSelector(
  visitSummaryListSelector,
  visits => visits.sortBy
);

export const visitSummaryDetailsSelector = (state: RootState) =>
  state.visitSummary.activeVisitDetails;

export const visitSummaryDetailsLoadingSelector = createSelector(
  visitSummaryDetailsSelector,
  activeVisitDetails => activeVisitDetails.isFetching
);

export const visitSummaryDetailsErrorSelector = createSelector(
  visitSummaryDetailsSelector,
  activeVisitDetails => activeVisitDetails.error
);

export const visitSummaryDetailsDataSelector = createSelector(
  visitSummaryDetailsSelector,
  activeVisitDetails => activeVisitDetails.data
);

export const visitSummaryDocumentSelector = (state: RootState) =>
  state.visitSummary.activeVisitDocument;

export const visitSummaryDocumentLoadingSelector = createSelector(
  visitSummaryDocumentSelector,
  activeVisitDocument => activeVisitDocument.isFetching
);

export const visitSummaryDocumentErrorSelector = createSelector(
  visitSummaryDocumentSelector,
  activeVisitDocument => activeVisitDocument.error
);

export const visitSummaryDocumentDataSelector = createSelector(
  visitSummaryDocumentSelector,
  activeVisitDocument => activeVisitDocument.data
);

export const visitSummaryDataSelector = createSelector(visitSummaryListSelector, visitSummaryList =>
  orderBy(dataSelector(visitSummaryList), ['date'], ['desc'])
);

export const visitSummaryFilteredAndSortedDataSelector = createSelector(
  [
    visitSummaryListDataSelector,
    visitSummaryFilterSelector,
    visitSummarySortBySelector,
    navSelector.currentNavigationRouteNameSelector
  ],
  (data, filter, sortBy, routeName) => {
    const activeSortBy = sortBy[routeName || ''];
    const activeFilter = filter[routeName || ''];

    let result = [...data];

    result = orderBy(result, ['date'], [activeSortBy?.value || 'desc']);

    if (activeFilter) {
      const { provider, location, visitType, startDate, endDate, year } = activeFilter;

      if (provider) {
        result = result.filter(r => r.primaryProvider?.providerId === provider);
      }

      if (location) {
        result = result.filter(r => r.locationObject.locationId === location);
      }

      if (visitType) {
        result = result.filter(r => r.visitType === visitType);
      }

      if (year) {
        result = result.filter(r => {
          return +year === dayjs(r.date).year();
        });
      } else if (startDate || endDate) {
        result = result.filter(r => {
          const date = dayjs(r.date).startOf('day');
          return isBetweenUnbounded(date, startDate, endDate);
        });
      }
    }

    return result;
  }
);

export const visitSummaryFiltersActiveSelector = createSelector(
  [visitSummaryFilterSelector, navSelector.currentNavigationRouteNameSelector],
  (filter, routeName) => filter[routeName] && Object.values(filter[routeName]).some(f => !!f)
);

export const visitSummaryHasVisitsSelector = createSelector(
  visitSummaryListDataSelector,
  data => data && !!data.length
);

export const visitSummaryListPollingSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.isPolling
);

export const visitSummaryScreenLoadingSelector = createSelector(
  [
    visitSummaryDetailsLoadingSelector,
    activeVisitVitalsLoadingSelector,
    activeEncounterMedicationsLoadingSelector
  ],
  (loadingVisitDetails, loadingVitals, loadingMedications) => {
    return loadingVisitDetails || loadingVitals || loadingMedications;
  }
);

export const visitSummaryDownloadsSelector = (state: RootState) => state.visitSummary.downloads;

export const visitSummaryMultipleDownloadsSelector = (state: RootState) =>
  state.visitSummary.multipleDownloads;

export const multipleDownloadsByCernerPersonId = (state: RootState) => {
  const cernerPersonId = fhirCurrentUserPatientId(state);
  const multipleDownloads = visitSummaryMultipleDownloadsSelector(state);
  return multipleDownloads?.[cernerPersonId];
};

/**
 * Share Visit Summary CCD
 */
export const shareVisitSummaryCcdSelector = (state: RootState) => state.visitSummary.shareCcd;

export const visitSummaryShareCcdDataSelector = createSelector(
  shareVisitSummaryCcdSelector,
  shareCcd => shareCcd.data
);

export const visitSummaryShareCcdSendingSelector = createSelector(
  shareVisitSummaryCcdSelector,
  shareCcd => shareCcd.isSending
);

export const visitSummaryListLastUpdatedSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.cacheUpdatedAt
);

/**
 * Share Multiple Visit Summary CCD
 */

export const shareMultipleVisitSummaryCcdSelector = (state: RootState) =>
  state.visitSummary.multipleShareCcd;

export const visitSummaryMultipleShareCcdDataSelector = createSelector(
  shareMultipleVisitSummaryCcdSelector,
  multipleShareCcd => multipleShareCcd.data
);

export const visitSummaryMultipleShareCcdSendingSelector = createSelector(
  shareMultipleVisitSummaryCcdSelector,
  multipleShareCcd => multipleShareCcd.isSending
);
